import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { FormGroup, Validators, FormControl, FormBuilder  } from '@angular/forms';
import { Partner } from 'src/app/models/services/audiences/dv360/partner';
import { Audience } from 'src/app/models/services/audiences/dv360/audience';
import { Advertiser } from 'src/app/models/services/audiences/dv360/advertiser';
import { DV360AudiencesService } from 'src/app/services/audiences/dv360-audiences.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';

@Component({
  selector: 'app-dv360-attributes',
  templateUrl: './dv360-attributes.component.html',
  styleUrls: ['./dv360-attributes.component.sass']
})
export class DV360AttributesComponent implements OnInit {

  @ViewChild('matAutocompletePartners') inputAutocompletePartners: ElementRef<HTMLInputElement>;
  @ViewChild('matAutocompleteAdvertisers') inputAutocompleteAdvertisers: ElementRef<HTMLInputElement>;
  @ViewChild('matAutocompleteAudiences') inputAutocompleteAudiences: ElementRef<HTMLInputElement>;


  @Input() form: FormGroup;
  @Input() updateValueAndValidity: () => void;

  partners: Partner[] = [];
  filteredPartners: Partner[] = [];
  advertisers: Advertiser[] = [];
  filteredAdvertisers: Advertiser[] = [];
  audiences: Audience[] = [];
  filteredAudiences: Audience[] = [];

  audienceSubTypes = [
    { value: 'NORMAL', label: 'Normal' },
    { value: 'REACH_FREQUENCY', label: 'Reach & Frequency' },
  ] as const;

  constructor(
    private dv360AudiencesServices: DV360AudiencesService,
    private spinnerService: NgxSpinnerService
  ) {}

  ngOnInit(): void {
    this.spinnerService.show("dialogSpinner");
    this.form.controls.newAudienceData.setValue('0');
    if (!this.form.contains('audienceSubType')) {
      this.form.addControl('audienceSubType', new FormControl('NORMAL', Validators.required));
    }
    this.dv360AudiencesServices.getPartners().subscribe(
      resp => {
        this.partners = resp;
        this.onNewAudienceSelectedChange();
        this.spinnerService.hide("dialogSpinner");
      }
    )
  }

  // CHANGE SELECTED
  onNewAudienceSelectedChange = (): void => {
    const isNew = String(this.form.controls.newAudienceData.value) === '1';

    this.form.controls.audienceId.setValue(null, { emitEvent: false });
    this.form.controls.audienceName.setValue('', { emitEvent: false });
    this.inputAutocompleteAudiences && (this.inputAutocompleteAudiences.nativeElement.value = '');

    if (isNew) {
      this.form.controls.audienceName.setValidators([Validators.required, Validators.minLength(2)]);
      this.form.controls.audienceId.clearValidators();
    } else {
      this.form.controls.audienceId.setValidators([Validators.required]);
      this.form.controls.audienceName.clearValidators();
    }

    this.form.controls.audienceId.updateValueAndValidity({ emitEvent: false });
    this.form.controls.audienceName.updateValueAndValidity({ emitEvent: false });

    this.updateValueAndValidity?.();
  };

  // Partners
  onPartnerChange = ($event: MatAutocompleteSelectedEvent): void => {
    this.spinnerService.show("dialogSpinner");
    this.resetAdvertiserControls([]);
    this.dv360AudiencesServices.getAdvertisers($event.option.value).subscribe(
      resp => {
        this.advertisers = resp;
        this.spinnerService.hide("dialogSpinner");
        this.inputAutocompletePartners?.nativeElement.blur();     
      }
    )
  }

  getPartnersDescription = (id?: string): string => {
    return this.partners.find(x => x.id == id)?.name;
  }

  resetPartnersFilter = (): void => {
    this.inputAutocompletePartners.nativeElement.value = '';
    this.resetPartnersControls(this.partners);
  }

  filterPartners = (): void => {
    const filterValue = this.inputAutocompletePartners.nativeElement.value.toLowerCase();
    this.resetPartnersControls(this.partners.filter(o => this.matchTerm(filterValue, o.id, o.name)));
  }

  resetPartnersControls = (filteredPartners: Partner[]): void => {
      this.filteredPartners = filteredPartners;
      if (filteredPartners == this.partners)
        this.form.controls.partnerId.setValue(null);
      if (!!this.inputAutocompleteAdvertisers)
        this.inputAutocompleteAdvertisers.nativeElement.value = '';
  
      this.advertisers = [];
      this.resetAdvertiserControls([]);
    }

  // BUSINESS -> ADVERTISERS
  onAdvertiserChange = ($event?: MatAutocompleteSelectedEvent): void => {
    const isNew = String(this.form.controls.newAudienceData.value) === '1';
    this.resetAudiencesControls([]);
    if (!isNew) {
      this.spinnerService.show("dialogSpinner");
      this.dv360AudiencesServices.getAudiences($event.option.value).subscribe(
        resp => {
          this.audiences = resp;     
          this.spinnerService.hide("dialogSpinner");
          this.inputAutocompleteAdvertisers.nativeElement.blur();          
          this.updateValueAndValidity();
        }
      )
    }
  };

  getAdvertiserDescription = (advertiserId: any): string => {
    if (!advertiserId) {
      return '';
    }
    var advertiser = this.advertisers.find(x => x.id == advertiserId);
    return advertiser.name && advertiser.name.trim() ? advertiser.name : advertiser.id;
  };

  resetAdvertisersFilter = (): void => {
    this.inputAutocompleteAdvertisers.nativeElement.value = '';
    this.resetAdvertiserControls(this.advertisers);
  };

  filterAdvertisers = (): void => {
    const term = this.inputAutocompleteAdvertisers.nativeElement.value || '';
    const filtered = this.advertisers.filter(o => this.matchTerm(term, o.id, o.name));
    this.resetAdvertiserControls(this.filteredAdvertisers = filtered);
  };


  resetAdvertiserControls = (filteredAdvertisers: Advertiser[]): void => {
    this.filteredAdvertisers = filteredAdvertisers;
    if (filteredAdvertisers == this.advertisers || filteredAdvertisers.length == 0)
      this.form.controls.advertiserId.setValue(null);          
    if (!!this.inputAutocompleteAudiences)
      this.inputAutocompleteAudiences.nativeElement.value = '';

    this.audiences = [];
    this.resetAudiencesControls([]);
  };


  //ADVERISERS -> AUDIENCES
  onAudienceChange = ($event?: MatAutocompleteSelectedEvent): void => {
    this.inputAutocompleteAdvertisers.nativeElement.blur();
    this.form.controls.audienceId.setValue($event.option.value);
    this.form.controls.audienceName.setValue(this.getAudienceDescription($event.option.value));
    this.updateValueAndValidity();
  };

  getAudienceDescription = (audienceId: string): string => {
    return this.audiences.find(x => x.id === audienceId)?.name;
  } 

  resetAudiencesFilter = (): void => {
    this.inputAutocompleteAudiences.nativeElement.value = '';
    this.resetAudiencesControls(this.audiences);
  }

  filterAudiences = (): void => {
    const filterValue = this.inputAutocompleteAudiences.nativeElement.value.toLowerCase();
    this.resetAudiencesControls(this.audiences.filter(o => `${o.id} - ${o.name}`.toLowerCase().includes(filterValue)));
    if (filterValue != this.form.controls.audienceName.value?.toLowerCase())
    {
      this.form.controls.audienceName.setValue(null);
      this.updateValueAndValidity();  
    } 
  }

  onAudienceDescriptionChange = (): void => {
    this.updateValueAndValidity();
  }

  resetAudiencesControls = (filteredAudiences: Audience[]): void => {
    this.filteredAudiences = filteredAudiences;
    if (filteredAudiences == this.audiences || filteredAudiences.length == 0)
      this.form.controls.audienceId.setValue(null);         
    this.updateValueAndValidity(); 
  }

  private matchTerm(term: string, ...fields: Array<string | number | null | undefined>): boolean {
    const t = (term ?? '').toString().toLowerCase().trim();
    if (!t) return true;
    return fields.some(f => (f ?? '').toString().toLowerCase().includes(t));
  }

}
